import { AxiosError } from "axios";
import { Rule, RuleType } from "../../components/interfaces/Rule";
import { Tag } from "../../components/interfaces/Tag";
import { RunResult } from "../../components/rules/RulesResultPanel";
import { isNonTerminal } from "../../components/rules/runStatus";
import { MULTI_BATCH, RUN_COMPLETION_WATCHDOG } from "../../components/rules/runConfig";
import { BatchResultsResponse, BatchScriptItem, ScriptResultItem } from "../../api/models/script-runner";
import {
  abortBatch,
  BatchResultsWithEtag,
  executeBatch,
  fetchBatchResults,
  joinBatch,
  streamBatch,
  StreamBatchHandlers,
} from "../../api/scriptRunner/ScriptRunner";
import * as uuid from "uuid";
import { attachTableFormats, readTableFormats } from "../../office/ContentControl";
import type { CellFormat } from "../../components/rules/tableTotals/tableTotalsResult.types";
import { selectValidationCoObjects } from "../../redux/feature/validation/validation-slice";
import { store } from "../../redux/store/store";
import { getDocumentPartMetadataFromCustomXml } from "./CustomXmlHelper";
import { formatMessageWithParamsDefault } from "./LanguageHelper";
import { createRowAggregation, ingestScriptResult, RowAggregationState } from "./runAggregation";
import { createBatchPool, packRules, RuleCost } from "./batchPool";
import {
  buildBatchScriptItems,
  buildResult,
  buildTableTotalsErrorResult,
  buildTableTotalsResult,
  buildUnifiedRunBatch,
  extractScriptIdFromSource,
  getRuleIdToTags,
  getTagIdToObjects,
  resolveValidTableTotalsRules,
  ValidTableTotalsRule,
} from "./RulesRunnerHelper";

/**
 * Streaming run-controller: collects data with the same helpers as the blocking `runRules` path,
 * builds one unified batch, and executes it via a single `executeBatch` + `streamBatch` (never
 * `executeRules`, which would split it into two batches). Per-row status is tracked through the
 * `runAggregation` contract, emitting incremental updates via `onRowUpdate`/`onRowsChanged`. A
 * batch settles from `batch-completed`; a lazy join supervisor recovers a silent/incomplete stream.
 *
 * Multi-batch support (`runConfig.ts`'s `MULTI_BATCH`, enabled by default): rules are packed into
 * one or more chunks (`batchPool.ts`'s `packRules`, at the rule level so a rule's scripts never
 * straddle two chunks), capped by rule/script/context-key count to mirror the backend's own limits,
 * and run through a bounded-concurrency pool. Each chunk is a self-contained `RunBatch` with its
 * own `executeBatch` call, `streamBatch` subscription, and fallback cascade. When disabled, exactly
 * one chunk is produced and starts immediately.
 */

/**
 * One `executeBatch`+`streamBatch` chunk for the current run (or, while `status === "QUEUED"`, a
 * pre-allocated `batchId`/`ruleIds` not yet started). Tracked so `cancelRun()` can abort every
 * in-flight batch and the multi-batch pool (`batchPool.ts`) can iterate chunks.
 */
export interface RunBatch {
  batchId: string;
  ruleIds: string[];
  /** Present once the SSE subscription starts. */
  stream?: { close: () => void };
  status: "QUEUED" | "RUNNING" | "COMPLETED" | "ABORTED";
}

/**
 * Exposes each batch's watchdog-cleanup callback (owned by `startChunk`'s closure) to
 * controller-level terminal paths (`cancelRun`, `finalize`). Weak keys avoid retaining an abandoned
 * controller via timer bookkeeping.
 */
const completionWatchdogCleanupByBatch = new WeakMap<RunBatch, () => void>();

function clearCompletionWatchdogForBatch(batch: RunBatch): void {
  const cleanup = completionWatchdogCleanupByBatch.get(batch);
  cleanup?.();
  completionWatchdogCleanupByBatch.delete(batch);
}

/**
 * Optional observer callbacks supplied to `runRulesStreaming`. `onRowUpdate`/`onRowsChanged` both
 * fire for every accepted row change — one with just the changed row, the other with the full
 * current snapshot in selection order.
 */
export interface RunControllerHandlers {
  /** Fired with the single row that just changed (seed, partial progress, or terminal). */
  onRowUpdate?: (row: RunResult) => void;
  /** Fired with the full current row snapshot (selection order) alongside every `onRowUpdate`. */
  onRowsChanged?: (rows: RunResult[]) => void;
  /** Fired once after every batch is authoritatively settled (or immediately for an empty selection). */
  onCompleted?: (rows: RunResult[]) => void;
  /**
   * Fired for an `executeBatch` rejection or an SSE transport error surfaced by `streamBatch`. Not
   * fired for benign no-ops (unknown scriptId, duplicate on an already-terminal row). Only surfaces
   * the raw error — the fallback cascade itself lives in `handleStreamFailure`.
   */
  onError?: (error: unknown) => void;
}

/**
 * Stateful holder for one streaming run's rows and in-flight batches, returned by
 * `runRulesStreaming` so callers can read the live snapshot. `cancelRun()` uses `batches` to abort
 * every in-flight batch and `getPendingRuleIds()`/`setRow()` to flip pending rows to CANCELED
 * (terminal rows are left untouched).
 */
export class RunController {
  readonly batches: RunBatch[] = [];

  private readonly order: string[] = [];
  private readonly rows = new Map<string, RunResult>();
  private finishedFlag = false;

  constructor(private readonly handlers: RunControllerHandlers = {}) {}

  /** Seeds every row once and fires a single `onRowsChanged` (no per-row `onRowUpdate`). */
  seedRows(initial: Map<string, RunResult>): void {
    for (const [ruleId, row] of initial) {
      this.order.push(ruleId);
      this.rows.set(ruleId, row);
    }
    this.handlers.onRowsChanged?.(this.getRows());
  }

  setRow(ruleId: string, row: RunResult): void {
    this.rows.set(ruleId, row);
    this.handlers.onRowUpdate?.(row);
    this.handlers.onRowsChanged?.(this.getRows());
  }

  getRunResult(ruleId: string): RunResult | undefined {
    return this.rows.get(ruleId);
  }

  getRows(): RunResult[] {
    const result: RunResult[] = [];
    for (const ruleId of this.order) {
      const row = this.rows.get(ruleId);
      if (row) result.push(row);
    }
    return result;
  }

  getPendingRuleIds(): string[] {
    return this.order.filter((ruleId) => {
      const row = this.rows.get(ruleId);
      return row ? isNonTerminal(row.info.status) : false;
    });
  }

  isFinished(): boolean {
    return this.finishedFlag;
  }

  finalize(): void {
    if (this.finishedFlag) return;
    this.finishedFlag = true;
    for (const batch of this.batches) {
      clearCompletionWatchdogForBatch(batch);
      if (batch.status === "RUNNING" || batch.status === "QUEUED") {
        batch.status = "COMPLETED";
      }
    }
    this.handlers.onCompleted?.(this.getRows());
  }

  /** Idempotent cancel-all: aborts every non-terminal batch and flips every pending row to
   * CANCELED. Abort calls run via `Promise.allSettled` so one rejecting batch never blocks the
   * others — the row is shown CANCELED locally regardless of the abort outcome. */
  async cancelRun(): Promise<void> {
    const abortRequests: Promise<unknown>[] = [];

    for (const batch of this.batches) {
      clearCompletionWatchdogForBatch(batch);
      if (batch.status === "COMPLETED" || batch.status === "ABORTED") continue;

      if (batch.status === "QUEUED") {
        batch.status = "ABORTED"; // never started — nothing server-side to abort.
        continue;
      }

      abortRequests.push(abortBatch(batch.batchId));
      batch.stream?.close();
      batch.status = "ABORTED";
    }

    for (const ruleId of this.getPendingRuleIds()) {
      const current = this.getRunResult(ruleId);
      if (current) {
        this.setRow(ruleId, cloneRowAsCanceled(current));
      }
    }

    await Promise.allSettled(abortRequests);
    this.finalize();
  }
}

function buildBatchId(): string {
  return uuid.v4();
}

/** Background format pass for TABLE_TOTALS rows, off the critical path. */
function scheduleBackgroundFormatPass(
  validTableTotalsRules: ValidTableTotalsRule[],
  coObjects: ReturnType<typeof selectValidationCoObjects>,
  controller: RunController,
  pendingFormatsByRuleId: Map<string, CellFormat[][] | undefined>
): void {
  if (validTableTotalsRules.length === 0) return;

  const ruleIdToContentControlId = new Map<string, string>();
  for (const { rule } of validTableTotalsRules) {
    const contentControlId = coObjects.find((o) => o.id === rule.tableTotals?.tableId)?.contentControlId;
    if (contentControlId !== undefined) {
      ruleIdToContentControlId.set(rule.id, String(contentControlId));
    }
  }

  const distinctIds = Array.from(new Set(ruleIdToContentControlId.values()));
  if (distinctIds.length === 0) return;

  readTableFormats(distinctIds)
    .then((formatsByContentControlId) => {
      for (const [ruleId, contentControlId] of ruleIdToContentControlId) {
        const formats = formatsByContentControlId.get(contentControlId);
        const row = controller.getRunResult(ruleId);
        if (row?.info.tableTotals) {
          attachTableFormats(row.info.tableTotals, formats);
          controller.setRow(ruleId, row);
        } else {
          // Row isn't terminal yet — stash for the row-building closure to attach once it is.
          pendingFormatsByRuleId.set(ruleId, formats);
        }
      }
    })
    .catch((error) => {
      console.error("[runRulesStreaming] background format pass rejected unexpectedly", error);
    });
}

const FALLBACK_ERROR_CAPTION = "cof.validation.message.failed.streamfallbackerror";
const FALLBACK_ERROR_DEFAULT = "Unable to retrieve the result for this rule after the run was interrupted.";

/** Forces a still-pending row to a terminal ERROR row, shared by every fallback/failure path so a
 * run never leaves a row spinning. CONSISTENCY rows get an empty `consistencyTags` since their
 * renderer requires that array. */
function buildFallbackErrorRow(rule: Rule, now: string): RunResult {
  if (rule.type === RuleType.TABLE_TOTALS) {
    return buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED");
  }
  return {
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: rule.type,
    info: {
      status: "ERROR",
      timestamp: now,
      message: formatMessageWithParamsDefault(FALLBACK_ERROR_CAPTION, FALLBACK_ERROR_DEFAULT, {}),
      consistencyTags: rule.type === RuleType.CONSISTENCY ? [] : undefined,
    },
  };
}

function cloneRowAsCanceled(row: RunResult): RunResult {
  return { ...row, info: { ...row.info, status: "CANCELED" } };
}

function settleStillPendingRows(
  controller: RunController,
  ruleById: Map<string, Rule>,
  now: string,
  ruleIds: string[],
  aborted: boolean,
  shouldContinue: () => boolean
): void {
  for (const ruleId of ruleIds) {
    if (!shouldContinue()) return;

    if (aborted) {
      const current = controller.getRunResult(ruleId);
      if (current) controller.setRow(ruleId, cloneRowAsCanceled(current));
    } else {
      const rule = ruleById.get(ruleId);
      if (rule) controller.setRow(ruleId, buildFallbackErrorRow(rule, now));
    }
  }
}

/**
 * Re-emits every still-unresolved row owned by `nextBatch` after the pool promotes that chunk from
 * QUEUED to RUNNING. Re-checks `nextBatch.status` each iteration since `controller.setRow` can
 * synchronously trigger `cancelRun()`, flipping the batch to ABORTED mid-loop.
 */
function promoteQueuedBatchRows(
  controller: RunController,
  nextBatch: RunBatch,
  aggregationByRuleId: Map<string, RowAggregationState>,
  ruleById: Map<string, Rule>,
  buildRow: (rule: Rule, aggregation: RowAggregationState) => RunResult
): void {
  for (const ruleId of nextBatch.ruleIds) {
    if (nextBatch.status !== "RUNNING") break; // a row callback may synchronously cancel the run.

    const aggregation = aggregationByRuleId.get(ruleId);
    const rule = ruleById.get(ruleId);
    if (aggregation?.status === "RUNNING" && rule) {
      controller.setRow(ruleId, buildRow(rule, aggregation));
    }
  }
}

/** Returns `true` when `error` is an `AxiosError`-shaped rejection carrying the given HTTP `status`.
 * Safe for non-axios errors, which simply evaluate to `false`. */
function isHttpStatus(error: unknown, status: number): boolean {
  return (error as AxiosError)?.response?.status === status;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Bounded ETag-poll budget for the `join`-`408` fallback branch: at most `RESULTS_POLL_MAX_ATTEMPTS`
 * attempts, `RESULTS_POLL_INTERVAL_MS` apart, so a batch that never converges still surfaces an
 * error within a few bounded seconds instead of polling indefinitely.
 */
const RESULTS_POLL_MAX_ATTEMPTS = 5;
const RESULTS_POLL_INTERVAL_MS = 1000;

/**
 * Bounded ETag-conditional poll loop against `GET /batch/{id}/results`, entered only once
 * `joinBatch` has timed out (`408`). Each attempt feeds back the previous ETag as `If-None-Match`
 * and applies any returned results immediately via `applyResult` (idempotent). Stops early once
 * every pending ruleId resolves, or the endpoint returns `404` (batch gone, no point retrying).
 * Never rejects: on budget exhaustion it resolves with whatever partial `response` was last
 * obtained plus a `lastError` that is always defined whenever full resolution was not reached.
 */
async function pollResultsWithinBudget(
  batchId: string,
  pendingRuleIds: () => string[],
  applyResult: (result: ScriptResultItem) => void
): Promise<{ response: BatchResultsWithEtag | undefined; lastError: unknown }> {
  let etag: string | undefined;
  let response: BatchResultsWithEtag | undefined;
  let lastError: unknown;

  for (let attempt = 1; attempt <= RESULTS_POLL_MAX_ATTEMPTS; attempt++) {
    try {
      const polled = await fetchBatchResults(batchId, etag ? { etag } : undefined);
      response = polled;
      etag = polled.etag ?? etag;
      lastError = undefined;
      for (const result of polled.results ?? []) applyResult(result);

      if (pendingRuleIds().length === 0) {
        return { response, lastError: undefined }; // fully resolved — stop polling immediately.
      }
    } catch (pollError) {
      lastError = pollError;
      if (isHttpStatus(pollError, 404)) {
        return { response: undefined, lastError }; // unknown batch — stop immediately, never retry.
      }
      // Otherwise (network hiccup, 408, ...) retry within budget.
    }

    if (attempt < RESULTS_POLL_MAX_ATTEMPTS) {
      await delay(RESULTS_POLL_INTERVAL_MS);
    }
  }

  return {
    response,
    lastError: lastError ?? new Error(`[runRulesStreaming] results polling budget exhausted for batch ${batchId}`),
  };
}

function pendingRuleIdsForBatch(controller: RunController, batch: RunBatch): string[] {
  return controller.getPendingRuleIds().filter((ruleId) => batch.ruleIds.includes(ruleId));
}

/**
 * `Last-Event-Id` resume point for a chunk's initial `streamBatch` subscription. Kept at `"0"`
 * since event ids start at 1, requesting replay of the entire per-batch journal; co-services
 * guarantees gap-free replay with `batch-completed` as the highest event id.
 */
const INITIAL_RESUME_EVENT_ID = "0";

/**
 * Resolves the fallback `response` once `joinBatch` rejects: a `408` enters the bounded ETag-poll
 * loop, a `404` (batch unknown) stops without calling `/results`, anything else makes one
 * `/results` attempt. `lastError` stays `joinError` when that single attempt succeeds.
 */
async function resolveJoinFallback(
  batchId: string,
  joinError: unknown,
  pendingRuleIds: () => string[],
  applyResult: (result: ScriptResultItem) => void
): Promise<{ response: BatchResultsWithEtag | undefined; lastError: unknown }> {
  if (isHttpStatus(joinError, 408)) {
    return pollResultsWithinBudget(batchId, pendingRuleIds, applyResult);
  }

  if (isHttpStatus(joinError, 404)) {
    return { response: undefined, lastError: joinError };
  }

  try {
    return { response: await fetchBatchResults(batchId), lastError: joinError };
  } catch (resultsError) {
    return { response: undefined, lastError: resultsError };
  }
}

/**
 * Core streaming implementation: collects data, builds one unified batch, and executes it as a
 * single `executeBatch` + `streamBatch` subscription, emitting per-row updates via `handlers`.
 * Returns the `RunController` once the batch is accepted (the background format pass keeps running
 * afterward, intentionally not awaited).
 *
 * Its data-collection phase can still reject before any row is seeded; the exported
 * `runRulesStreaming` wraps this in a `try`/`catch` so such a failure converges every row to a
 * terminal error instead of leaving a caller's placeholder rows stuck RUNNING.
 */
async function runRulesStreamingCore(
  selectedRuleIds: string[],
  allRules: Rule[],
  allTags: Tag[],
  handlers: RunControllerHandlers = {}
): Promise<RunController> {
  const controller = new RunController(handlers);

  const selectedRules = selectedRuleIds
    .map((id) => allRules.find((r) => r.id === id))
    .filter((r): r is Rule => Boolean(r));

  if (!selectedRules.length) {
    controller.finalize();
    return controller;
  }

  // 1. Data collection — same sequence/helpers as the blocking `runRules` path. `notShowLoading` is
  // needed here because this is the only call on this path that can reach axios (its
  // legacy-document fallback); the streaming path must never show the global loading overlay. The
  // blocking path calls the same function with no args and legitimately keeps the loader.
  const ruleIdToTags = getRuleIdToTags(selectedRules, allTags);
  const tagIdToObjects = await getTagIdToObjects([...ruleIdToTags.values()].flat());
  const documentMetadata = await getDocumentPartMetadataFromCustomXml({ params: { notShowLoading: true } });

  const tableTotalsRules = selectedRules.filter((r) => r.type === RuleType.TABLE_TOTALS);
  const coObjects = selectValidationCoObjects(store.getState());
  const now = new Date().toISOString();
  // resultMap is a throwaway here — buildUnifiedRunBatch independently synthesizes an early ERROR
  // for any excluded TABLE_TOTALS rule, so these writes are never consulted on this path.
  const validTableTotalsRules = await resolveValidTableTotalsRules(tableTotalsRules, coObjects, new Map(), now);

  // 2. One unified batch.
  const unifiedBatch = buildUnifiedRunBatch(
    selectedRules,
    ruleIdToTags,
    tagIdToObjects,
    documentMetadata,
    validTableTotalsRules
  );

  // errorKindMap isn't part of UnifiedRunBatch by design — derive it separately for terminal-row
  // rendering parity with the blocking path (a pure, side-effect-free recomputation).
  const { errorKindMap } = buildBatchScriptItems(selectedRules, ruleIdToTags, tagIdToObjects, documentMetadata);

  const validTableTotalsRuleMap = new Map(validTableTotalsRules.map((v) => [v.rule.id, v] as const));
  const ruleById = new Map(selectedRules.map((r) => [r.id, r] as const));

  const aggregationByRuleId = new Map<string, RowAggregationState>();
  const rawResultsByRuleId = new Map<string, Map<string, ScriptResultItem>>();
  const scriptIdToRuleId = new Map<string, string>();
  const pendingFormatsByRuleId = new Map<string, CellFormat[][] | undefined>();
  // Which chunk a ruleId is assigned to — lets `buildRow` tell a still-unresolved row apart as
  // RUNNING (chunk started) vs QUEUED (chunk not started yet).
  const ruleIdToBatch = new Map<string, RunBatch>();

  /**
   * Builds a row's full terminal `RunResult`, reusing the same content-builders the blocking
   * `runRules` path uses (`buildResult`, and for TABLE_TOTALS `buildTableTotalsResult`/
   * `buildTableTotalsErrorResult`), fed with only this row's own accumulated raw results.
   */
  const buildTerminalRow = (rule: Rule): RunResult => {
    const rawResults = Array.from(rawResultsByRuleId.get(rule.id)?.values() ?? []);
    const tableTotalsResultMap = new Map<string, RunResult>();

    if (rule.type === RuleType.TABLE_TOTALS) {
      const validEntry = validTableTotalsRuleMap.get(rule.id);
      const scriptResult = rawResults[0];
      const built =
        validEntry && scriptResult
          ? buildTableTotalsResult(
              validEntry.rule,
              validEntry.spec,
              validEntry.grid,
              scriptResult,
              now,
              validEntry.tableName
            )
          : buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED");

      if (built.info.tableTotals && pendingFormatsByRuleId.has(rule.id)) {
        attachTableFormats(built.info.tableTotals, pendingFormatsByRuleId.get(rule.id));
        pendingFormatsByRuleId.delete(rule.id);
      }
      tableTotalsResultMap.set(rule.id, built);
    }

    // `batchId` is purely informational (buildResult only reads `.results`), sourced from this
    // row's own chunk when available.
    const batchResultsLike: BatchResultsResponse = {
      batchId: ruleIdToBatch.get(rule.id)?.batchId ?? buildBatchId(),
      results: rawResults,
    };
    return buildResult(batchResultsLike, [rule], ruleIdToTags, tagIdToObjects, errorKindMap, tableTotalsResultMap)[0];
  };

  /**
   * Minimal RUNNING/QUEUED row while non-terminal; full content (via `buildTerminalRow`) once
   * terminal. A still-unresolved row shows QUEUED instead of RUNNING while its own chunk
   * (`ruleIdToBatch`) hasn't started yet.
   */
  const buildRow = (rule: Rule, aggregation: RowAggregationState): RunResult => {
    if (aggregation.status === "RUNNING") {
      const status = ruleIdToBatch.get(rule.id)?.status === "QUEUED" ? "QUEUED" : "RUNNING";
      return {
        id: `${rule.id}-${now}`,
        ruleId: rule.id,
        title: rule.title,
        ruleType: rule.type,
        info: { status, timestamp: now },
      };
    }
    return buildTerminalRow(rule);
  };

  // Pack the selection into one or more self-contained chunks. Disabled (`MULTI_BATCH.enabled ===
  // false`): always exactly one chunk holding every selected rule, matching the original
  // single-batch behavior. Enabled (the default): rules are packed at the rule level by
  // `packRules`, closing a chunk once adding the next rule would exceed the rule-count,
  // script-count, or context-key-union cap — so a rule's scriptIds always land in the same chunk.
  const costOf = (rule: Rule): RuleCost => ({
    id: rule.id,
    scriptCount: (unifiedBatch.scriptsByRuleId.get(rule.id) ?? []).length,
    contextKeys: unifiedBatch.contextKeysByRuleId.get(rule.id) ?? [],
  });
  const ruleChunks: Rule[][] = MULTI_BATCH.enabled
    ? packRules(selectedRules, costOf, {
        maxRulesPerBatch: MULTI_BATCH.maxRulesPerBatch,
        maxScriptsPerBatch: MULTI_BATCH.maxScriptsPerBatch,
        maxContextKeysPerBatch: MULTI_BATCH.maxContextKeysPerBatch,
      })
    : [selectedRules];
  const concurrency = MULTI_BATCH.enabled ? MULTI_BATCH.maxConcurrentBatches : ruleChunks.length;

  const chunkBatches: RunBatch[] = ruleChunks.map((rules) => ({
    batchId: buildBatchId(),
    ruleIds: rules.map((r) => r.id),
    status: "QUEUED",
  }));
  const pool = createBatchPool(
    chunkBatches.map((b) => b.batchId),
    concurrency
  );
  const batchesById = new Map(chunkBatches.map((b) => [b.batchId, b] as const));
  const initialBatchIds = new Set(pool.initialIds);

  const registerChunks = (): void => {
    for (const chunkBatch of chunkBatches) {
      if (initialBatchIds.has(chunkBatch.batchId)) chunkBatch.status = "RUNNING";
      controller.batches.push(chunkBatch);
      for (const ruleId of chunkBatch.ruleIds) ruleIdToBatch.set(ruleId, chunkBatch);
    }
  };
  registerChunks();

  // Each chunk's own scripts, filtered from the unified `scripts` array (preserves original order).
  const chunkScriptsByBatchId = new Map<string, BatchScriptItem[]>(
    chunkBatches.map((chunkBatch) => {
      const expectedIds = new Set<string>();
      for (const ruleId of chunkBatch.ruleIds) {
        for (const scriptId of unifiedBatch.expectedScriptIds.get(ruleId) ?? []) expectedIds.add(scriptId);
      }
      const scripts = unifiedBatch.scripts.filter((item) => {
        const scriptId = extractScriptIdFromSource(item.source);
        return scriptId !== undefined && expectedIds.has(scriptId);
      });
      return [chunkBatch.batchId, scripts] as const;
    })
  );

  // Each chunk's own context: only the keys its own rules need, picked out of the full merged
  // `unifiedBatch.context`. This is what makes `MULTI_BATCH.maxContextKeysPerBatch` meaningful —
  // without it, every chunk would receive the full-selection context regardless of its own rules.
  const chunkContextByBatchId = new Map<string, Record<string, any>>(
    chunkBatches.map((chunkBatch) => {
      const keys = new Set<string>();
      for (const ruleId of chunkBatch.ruleIds) {
        for (const key of unifiedBatch.contextKeysByRuleId.get(ruleId) ?? []) keys.add(key);
      }
      const context: Record<string, any> = {};
      for (const key of keys) {
        if (key in unifiedBatch.context) context[key] = unifiedBatch.context[key];
      }
      return [chunkBatch.batchId, context] as const;
    })
  );

  // Seed every row synchronously, before any network call, populating `scriptIdToRuleId`/
  // `rawResultsByRuleId`/`aggregationByRuleId` along the way.
  const seedInitialRows = (): Map<string, RunResult> => {
    const initialRows = new Map<string, RunResult>();
    for (const rule of selectedRules) {
      const expected = unifiedBatch.expectedScriptIds.get(rule.id) ?? [];
      for (const scriptId of expected) scriptIdToRuleId.set(scriptId, rule.id);

      const earlyForRow = unifiedBatch.earlyResults.filter((r) => r.scriptId && expected.includes(r.scriptId));
      const rawMap = new Map<string, ScriptResultItem>();
      for (const early of earlyForRow) {
        if (early.scriptId) rawMap.set(early.scriptId, early);
      }
      rawResultsByRuleId.set(rule.id, rawMap);

      // A row with zero expected scriptIds (e.g. a CONSISTENCY rule with no tags) can never be
      // resolved through aggregation — mirror the blocking path's identical "zero scripts, zero
      // results" handling by building the terminal row directly; such a row is also never QUEUED
      // since it needs no batch execution.
      if (expected.length === 0) {
        initialRows.set(rule.id, buildTerminalRow(rule));
        continue;
      }

      const aggregation = createRowAggregation(expected, earlyForRow);
      aggregationByRuleId.set(rule.id, aggregation);
      initialRows.set(rule.id, buildRow(rule, aggregation));
    }
    return initialRows;
  };
  controller.seedRows(seedInitialRows());

  // Fallback cascade: SSE error -> joinBatch -> paged /results -> surfaced error.
  /**
   * Called once a chunk's own batch reaches a terminal state (COMPLETED/ABORTED), from whichever of
   * `startChunk`'s settle points got there first. Frees that chunk's pool slot and, if another chunk
   * is still queued, flips its still-unresolved rows QUEUED -> RUNNING and starts it. The run only
   * finalizes once every chunk has reached a terminal state.
   */
  const advancePool = (settledBatchId: string): void => {
    const nextBatchId = pool.release(settledBatchId);
    const nextBatch = nextBatchId !== undefined ? batchesById.get(nextBatchId) : undefined;
    if (nextBatch?.status === "QUEUED") {
      nextBatch.status = "RUNNING";
      promoteQueuedBatchRows(controller, nextBatch, aggregationByRuleId, ruleById, buildRow);
      if (nextBatch.status === "RUNNING") {
        void startChunk(nextBatch).catch((unexpected) => {
          console.error("[runRulesStreaming] failed to start the next queued chunk", unexpected);
        });
      }
    }

    if (controller.batches.every((b) => b.status === "COMPLETED" || b.status === "ABORTED")) {
      // Safety net: a run must never finalize with a row still RUNNING/QUEUED. Every batch has
      // already reached a terminal state here, so nothing could resolve a still-pending row further.
      for (const ruleId of controller.getPendingRuleIds()) {
        const rule = ruleById.get(ruleId);
        const current = controller.getRunResult(ruleId);
        if (rule && current && isNonTerminal(current.info.status)) {
          controller.setRow(ruleId, buildFallbackErrorRow(rule, now));
        }
      }
      controller.finalize();
    }
  };

  /**
   * Starts one chunk end-to-end: `executeBatch`, background format pass, `streamBatch` subscription
   * with a lazy join supervisor, and the fallback cascade — all funneling into `settleBatch`.
   */
  const startChunk = async (batch: RunBatch): Promise<void> => {
    if (batch.status !== "RUNNING") return; // cancellation may win before a queued start callback runs.

    const scripts = chunkScriptsByBatchId.get(batch.batchId) ?? [];
    const context = chunkContextByBatchId.get(batch.batchId) ?? {};

    /**
     * Applies one raw script result to its row's aggregation and re-emits the row if it changed.
     * Idempotent, so a result already delivered via one transport (SSE, join, poll) is a no-op if
     * applied again by another.
     */
    const applyScriptResult = (result: ScriptResultItem): void => {
      // cancelRun() marks this batch ABORTED synchronously before flipping its rows to CANCELED, so
      // a late/in-flight script-result can never resurrect an already-canceled row.
      if (batch.status !== "RUNNING") return;

      // A compile-error result carries no `scriptId`, only a positional `index` into this chunk's
      // own `scripts` array — recover it the same way the blocking path does, via
      // `extractScriptIdFromSource`. Without this the result would fail the lookup below and its
      // row would never resolve.
      if (!result.scriptId && result.index != null) {
        result.scriptId = extractScriptIdFromSource(scripts[result.index]?.source);
      }

      const ruleId = scriptIdToRuleId.get(result.scriptId ?? "");
      if (!ruleId) return; // unknown/removed row — ignored.

      const aggregation = aggregationByRuleId.get(ruleId);
      if (aggregation?.status !== "RUNNING") return; // already terminal.

      const nextAggregation = ingestScriptResult(aggregation, result);
      if (nextAggregation === aggregation) return; // referential no-op (unknown/not-expected).

      aggregationByRuleId.set(ruleId, nextAggregation);
      if (result.scriptId) {
        rawResultsByRuleId.get(ruleId)?.set(result.scriptId, result);
      }

      const rule = ruleById.get(ruleId);
      if (!rule) return;

      controller.setRow(ruleId, buildRow(rule, nextAggregation));
    };

    /**
     * The sole terminalizer/pool-releaser for this batch, synchronous by design. Re-checks
     * `batch.status` after every step because `applyScriptResult`/`setRow` can synchronously
     * trigger `cancelRun()` mid-call: (1) skip if already settled; (2) apply results; (3) re-check;
     * (4) force still-pending rows to CANCELED (if ABORTED) or the fallback ERROR row; (5) re-check;
     * (6) set this batch's terminal status, close its stream, and release the pool.
     */
    const settleBatch = (response: BatchResultsResponse): void => {
      if (batch.status !== "RUNNING") return;

      clearCompletionWatchdogForBatch(batch);
      for (const result of response.results ?? []) applyScriptResult(result);

      if (batch.status !== "RUNNING") return;
      if (response.status !== "COMPLETED" && response.status !== "ABORTED") return;

      const aborted = response.status === "ABORTED";
      settleStillPendingRows(
        controller,
        ruleById,
        now,
        pendingRuleIdsForBatch(controller, batch),
        aborted,
        () => batch.status === "RUNNING"
      );

      if (batch.status !== "RUNNING") return;

      batch.status = aborted ? "ABORTED" : "COMPLETED";
      batch.stream?.close();
      advancePool(batch.batchId);
    };

    // A chunk with no scripts at all (every rule resolved early — zero expected scriptIds) has
    // nothing for `executeBatch` to queue. Settle it immediately as COMPLETED instead of calling
    // `executeBatch`/`streamBatch`: every row it owns was already seeded terminal, so
    // `settleBatch`'s own fallback loop is a no-op here, kept only as a defensive backstop.
    if (scripts.length === 0) {
      settleBatch({ batchId: batch.batchId, status: "COMPLETED" });
      return;
    }

    try {
      // The streaming path shows an immediate panel with per-row RUNNING/QUEUED placeholders, so
      // the global blocking loading overlay must stay suppressed.
      await executeBatch(batch.batchId, { context, scripts }, { params: { notShowLoading: true } });
    } catch (error) {
      if (batch.status !== "RUNNING") return; // settled (e.g. cancelRun()) while this await was in flight.

      // An `executeBatch` rejection means no server-side batch was ever created, so nothing could
      // ever resolve this chunk's rows — force every still-pending ruleId to a terminal error row.
      const stillPendingRuleIds = pendingRuleIdsForBatch(controller, batch);
      settleStillPendingRows(controller, ruleById, now, stillPendingRuleIds, false, () => batch.status === "RUNNING");

      if (batch.status !== "RUNNING") return; // a row callback may have canceled the run.
      handlers.onError?.(error);
      if (batch.status !== "RUNNING") return; // the error callback may also cancel synchronously.
      // "ABORTED" here is safe even though every row is already ERROR: settleBatch's
      // ABORTED-conversion loop only touches still-pending rows, and none remain.
      settleBatch({ batchId: batch.batchId, status: "ABORTED" });
      return;
    }

    // A concurrent cancelRun() may have already settled this batch while executeBatch was in
    // flight — subscribing to a stream for it now would only leak a connection.
    if (batch.status !== "RUNNING") return;

    const chunkValidTableTotalsRules = validTableTotalsRules.filter((v) => batch.ruleIds.includes(v.rule.id));
    scheduleBackgroundFormatPass(chunkValidTableTotalsRules, coObjects, controller, pendingFormatsByRuleId);

    /**
     * SSE fallback cascade for this batch: `streamBatch` transport error -> `joinBatch` ->
     * `resolveJoinFallback` -> surfaced error. Triggered by a live SSE transport error or the join
     * supervisor's own failure. Results are fed through the same `applyScriptResult` every
     * transport uses; any row still pending once the cascade ends is forced to a terminal ERROR row.
     *
     * Unlike the join supervisor's silent long-poll, this cascade's network calls are left
     * unsuppressed — it only runs after a genuine failure, so the loading UX stays intentional.
     */
    const handleStreamFailure = async (error: unknown): Promise<void> => {
      if (batch.status !== "RUNNING") return;

      clearCompletionWatchdogForBatch(batch);
      // Tear down the failing stream first to avoid a browser-level auto-reconnect racing the
      // fallback below (which would double-handle the same results).
      batch.stream?.close();

      let lastError = error;
      let response: BatchResultsWithEtag | undefined;

      try {
        response = await joinBatch(batch.batchId);
      } catch (joinError) {
        const fallback = await resolveJoinFallback(
          batch.batchId,
          joinError,
          () => pendingRuleIdsForBatch(controller, batch),
          applyScriptResult
        );
        response = fallback.response;
        lastError = fallback.lastError;
      }

      // A concurrent cancelRun() may have already settled this batch and canceled its rows — defer
      // to it entirely rather than fighting its outcome.
      if (batch.status !== "RUNNING") return;

      // A terminal response is authoritative. In particular, ABORTED must preserve every real
      // result it contains and convert only the still-missing rows to CANCELED; pre-building ERROR
      // rows before `settleBatch` saw that status would destroy that distinction.
      if (response?.status === "COMPLETED" || response?.status === "ABORTED") {
        settleBatch(response);
        return;
      }

      // Apply whatever the cascade obtained before deciding whether it fully resolved this batch's
      // rows. This branch is only for a non-terminal `/results` snapshot.
      for (const result of response?.results ?? []) applyScriptResult(result);
      if (batch.status !== "RUNNING") return; // applying a result can synchronously cancel the run.

      // Computed before the fallback below forces anything still pending to a terminal row, so
      // "resolved everything" reflects only what the live results achieved.
      const stillPendingRuleIds = pendingRuleIdsForBatch(controller, batch);
      settleStillPendingRows(controller, ruleById, now, stillPendingRuleIds, false, () => batch.status === "RUNNING");

      if (batch.status !== "RUNNING") return; // a fallback row callback may have canceled the run.
      const resolvedEverything = response !== undefined && stillPendingRuleIds.length === 0;
      if (!resolvedEverything) handlers.onError?.(lastError);
      if (batch.status !== "RUNNING") return; // the surfaced error callback may cancel synchronously.

      // "ABORTED" here is safe even when unresolved despite every row already being ERROR:
      // settleBatch's ABORTED-conversion loop only touches still-pending rows, and none remain.
      settleBatch({ batchId: batch.batchId, status: resolvedEverything ? "COMPLETED" : "ABORTED" });
    };

    // Subscribe to this chunk's own SSE stream and maintain per-row aggregation. `batch-completed`
    // is the normal, zero-network settlement signal now that replay is gap-free. A silence watchdog
    // still guarantees convergence if this stream emits nothing at all; `completionObserved`
    // suppresses both that watchdog and a redundant transport-failure cascade once seen.
    let completionObserved = false;
    let completionWatchdog: ReturnType<typeof setTimeout> | undefined;
    let joinSupervisorStarted = false;

    const clearCompletionWatchdog = (): void => {
      if (completionWatchdog === undefined) return;
      clearTimeout(completionWatchdog);
      completionWatchdog = undefined;
    };
    completionWatchdogCleanupByBatch.set(batch, clearCompletionWatchdog);

    /**
     * Independent supervisor guaranteeing this batch converges even if the live stream never
     * delivers a single event. Long-polls `GET /batch/{id}/join` with `notShowLoading: true` (runs
     * silently in the background). A `408` (timed out, no terminal state) loops indefinitely — this
     * supervisor IS the run's own convergence guarantee, so it must keep asking. Any other failure
     * defers to `handleStreamFailure`; a terminal `200` settles the batch via `settleBatch`.
     */
    const joinSupervisor = async (): Promise<void> => {
      for (;;) {
        if (batch.status !== "RUNNING") return;

        let response: BatchResultsResponse;
        try {
          response = await joinBatch(batch.batchId, 30, { timeout: 40000, params: { notShowLoading: true } });
        } catch (joinError) {
          if (batch.status !== "RUNNING") return; // re-check after every awaited join.

          if (isHttpStatus(joinError, 408)) {
            continue; // long-poll timed out without a terminal state — ask again, unbounded.
          }

          handleStreamFailure(joinError).catch((unexpected) => {
            console.error("[runRulesStreaming] join supervisor's fallback cascade rejected unexpectedly", unexpected);
          });
          return;
        }

        if (batch.status !== "RUNNING") return; // re-check after every awaited join.

        settleBatch(response);
        if (batch.status !== "RUNNING") return;
        // Defensive only: `/join` is specified to return 200 only for a terminal batch. If a
        // non-terminal snapshot ever slips through, keep supervising rather than stranding the run.
      }
    };

    /**
     * Starts the supervisor at most once for this batch. Both the watchdog callback and
     * `onCompleted` can reach this in the same turn; setting the flag synchronously before launching
     * the promise makes that race deterministic and prevents duplicate `/join` loops.
     */
    const startJoinSupervisor = (): void => {
      if (joinSupervisorStarted || batch.status !== "RUNNING") return;
      joinSupervisorStarted = true;
      clearCompletionWatchdogForBatch(batch);
      joinSupervisor().catch((unexpected) => {
        console.error("[runRulesStreaming] join supervisor rejected unexpectedly", unexpected);
      });
    };

    /**
     * Arms (or re-arms) the silence window. Script results and progress are proof that the stream is
     * alive, so both reset it; once completion was observed or recovery already started, no further
     * watchdog handle is useful.
     */
    const armCompletionWatchdog = (): void => {
      clearCompletionWatchdog();
      if (batch.status !== "RUNNING" || completionObserved || joinSupervisorStarted) return;

      completionWatchdog = setTimeout(() => {
        completionWatchdog = undefined;
        if (batch.status !== "RUNNING" || completionObserved) return;
        startJoinSupervisor();
      }, RUN_COMPLETION_WATCHDOG.startJoinAfterSilenceMs);
    };

    const streamHandlers: StreamBatchHandlers = {
      onScriptResult: (result) => {
        applyScriptResult(result);
        armCompletionWatchdog();
      },
      onProgress: () => {
        armCompletionWatchdog();
      },
      onCompleted: () => {
        if (batch.status !== "RUNNING") return; // late/duplicate event after settle - benign no-op.
        completionObserved = true;
        clearCompletionWatchdogForBatch(batch);

        // `streamBatch` deliberately does NOT auto-close on `batch-completed`; close synchronously
        // before either settlement path so the backend's own end-of-stream cannot trigger a browser
        // reconnect. The real handle is idempotent, so `settleBatch` closing it again is harmless.
        batch.stream?.close();

        if (batch.status !== "RUNNING") return;
        if (pendingRuleIdsForBatch(controller, batch).length === 0) {
          // Gap-free replay guarantees every script result precedes completion. With no pending row,
          // the event itself is sufficient authority: settle locally and make ZERO network calls.
          settleBatch({ batchId: batch.batchId, status: "COMPLETED" });
          return;
        }

        // Completion is terminal but one or more rows are missing locally. Reuse the single existing
        // recovery mechanism; `/join` returns its authoritative terminal response immediately.
        startJoinSupervisor();
      },
      onError: (error) => {
        // A transport error is a benign no-op once a live `batch-completed` was already observed for
        // this batch (that event closed the stream itself — see `onCompleted` above — so a subsequent
        // transport `error` is just the connection noticing its own, already-expected, teardown) OR
        // once this batch is no longer RUNNING (settled some other way already, e.g. `cancelRun()` or
        // the lazy join supervisor).
        if (completionObserved || batch.status !== "RUNNING") return;

        // Fire-and-forget: onError is synchronous but the cascade is async; `.catch()` is a
        // defensive backstop against an unhandled rejection.
        handleStreamFailure(error).catch((unexpected) => {
          console.error("[runRulesStreaming] fallback cascade rejected unexpectedly", unexpected);
        });
      },
    };

    // Subscribe with an active replay cursor, then arm the silence watchdog. `streamBatch` returns
    // its close handle synchronously even though listener setup awaits auth headers.
    batch.stream = streamBatch(batch.batchId, streamHandlers, { lastEventId: INITIAL_RESUME_EVENT_ID });
    armCompletionWatchdog();
  };

  // Start every chunk the pool allows immediately, awaited so the returned controller reflects a
  // settled state for each. The rest stay QUEUED until a running chunk's `advancePool` starts them.
  const initialBatches = pool.initialIds.map((id) => batchesById.get(id)).filter((b): b is RunBatch => b !== undefined);
  await Promise.all(initialBatches.map((batch) => startChunk(batch)));

  return controller;
}

/**
 * Builds a fresh, already-finalized `RunController` whose rows are all terminal ERROR — used when
 * `runRulesStreamingCore`'s pre-batch data-collection phase rejects before any row was seeded.
 * Reuses `buildFallbackErrorRow`, the same helper every other convergence fix in this module uses.
 */
function buildFailedController(
  selectedRuleIds: string[],
  allRules: Rule[],
  handlers: RunControllerHandlers,
  error: unknown
): RunController {
  const controller = new RunController(handlers);
  const now = new Date().toISOString();
  const selectedRules = selectedRuleIds
    .map((id) => allRules.find((r) => r.id === id))
    .filter((r): r is Rule => Boolean(r));

  const fallbackRows = new Map<string, RunResult>();
  for (const rule of selectedRules) {
    fallbackRows.set(rule.id, buildFallbackErrorRow(rule, now));
  }

  controller.seedRows(fallbackRows);
  handlers.onError?.(error);
  controller.finalize();
  return controller;
}

/**
 * Public entry point. Delegates to `runRulesStreamingCore`; the only addition is a `try`/`catch` so
 * a data-collection-phase failure (before any row is seeded) converges every selected rule to a
 * terminal error row instead of leaving this promise rejected — a caller that optimistically
 * renders its own placeholder RUNNING rows before awaiting this call would otherwise be stuck
 * showing RUNNING forever.
 */
export async function runRulesStreaming(
  selectedRuleIds: string[],
  allRules: Rule[],
  allTags: Tag[],
  handlers: RunControllerHandlers = {}
): Promise<RunController> {
  try {
    return await runRulesStreamingCore(selectedRuleIds, allRules, allTags, handlers);
  } catch (error) {
    console.error("[runRulesStreaming] pre-batch data-collection phase failed — converging to error rows", error);
    return buildFailedController(selectedRuleIds, allRules, handlers, error);
  }
}
